<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { Button, ButtonGroup, Input } from '@sveltestrap/sveltestrap';

  import { DatePicker } from '@client/components/datepicker';
  import { ListField } from '@client/components/fields/general';
  import { FolderTreeDropdown } from '@client/components/folder-tree-dropdown';
  import { Icon } from '@client/components/icon';
  import { QueryParams } from '@client/components/query-params';
  import { Select } from '@client/components/select';
  import { Switch } from '@client/components/switch';

  import { sanitizeInputValue } from '@client/utils/sanitize';

  import { AD_TYPES, LINK_TYPES } from '@client/enums';

  import { formatBytes, secondsToHms } from '@common/utils/numbers';
  import { isValidDomain, isValidEmail } from '@common/utils/validation';

  const { basepath } = getContext('config');

  // Campaign data
  export let campaign;

  // Schedule state
  export let startDate = '';
  export let endDate = '';
  export let isOpen = false;
  export let defaultMonth = new Date().getMonth();
  export let defaultYear = new Date().getFullYear();
  export let dateInputValue = '';

  // Tags state
  export let tags = [];
  export let selectedTags = [];
  export let filterText = '';

  // Categories state
  export let categories = [];
  export let selectedCategories = [];

  // Folders state
  export let folders = [];
  export let selectedFolders = [];
  export let selectedLabels = [];

  // Assets state
  export let documentItems = [];
  export let selectedDocument = '';
  export let videoItems = [];
  export let selectedVideo = '';

  // Files state
  export let files = [];

  // Validation state
  export let validationError = false;
  export let errorMessage = '';

  // Targeting state
  export let recipients = [];
  export let domains = [];

  // Event handlers (passed from parent)
  export let toggleDatePicker = () => {};
  export let onDateInputChange = () => {};
  export let onDatePickerChange = () => {};
  export let onTagChange = () => {};
  export let onTagClear = () => {};
  export let onCategoryChange = () => {};
  export let onSelect = () => {};
  export let onClickAction = () => {};
  export let onDocumentChange = () => {};
  export let onVideoChange = () => {};
  export let onValidation = () => {};
  export let onUrlChange = () => {};
  export let onParamsChange = () => {};
  export let onAdType = () => {};
  export let onFileUpload = () => {};
  export let onRemoveFile = () => {};
</script>

<h2>{$format('label.CAMPAIGN')}</h2>
<div class="section">
  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.NAME')}</b>
      <p>{$format('label.DESCRIPTION.NAME_CAMPAIGN')}</p>
    </div>
    <div class="section-controls">
      <Input
        type="text"
        bind:value={campaign.rulename}
        on:keyup={() => {
          campaign.rulename = sanitizeInputValue(campaign.rulename);
        }}
      />
    </div>
  </div>

  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.SCHEDULE')}</b>
      <p>{$format('label.DESCRIPTION.SCHEDULE_CAMPAIGN')}</p>
    </div>
    <div class="section-controls">
      <Switch bind:checked={campaign.enableschedule} />
    </div>
  </div>

  {#if campaign.enableschedule}
    <div class="section-item">
      <div class="section-info">
        <b>{$format('label.SCHEDULE_DATES')}</b>
        <p>{$format('label.DESCRIPTION.SCHEDULE_DATES')}</p>
      </div>
      <div class="section-controls">
        <div class="schedule-dates">
          <Input
            type="text"
            on:click={toggleDatePicker}
            on:change={onDateInputChange}
            class="dates"
            bind:value={dateInputValue}
          />
          <DatePicker
            align="right"
            isRange
            showTimePicker
            enableFutureDates
            isMultipane
            showPresets={false}
            onDateChange={onDatePickerChange}
            bind:startDate
            bind:isOpen
            bind:defaultMonth
            bind:defaultYear
            bind:endDate
          />

          {#if !startDate && !endDate}
            <div class="error">{$format('label.SCHEDULE_DATES_ERROR_NO_START_END')}</div>
          {:else if !startDate}
            <div class="error">{$format('label.SCHEDULE_DATES_ERROR_NO_START')}</div>
          {:else if !endDate}
            <div class="error">{$format('label.SCHEDULE_DATES_ERROR_NO_END')}</div>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.TAGS')}</b>
      <p>{$format('label.DESCRIPTION.TAGS_CAMPAIGN')}</p>
    </div>
    <div class="section-controls" on:keyup={onTagChange} role="button" tabindex="0">
      <Select
        bind:items={tags}
        bind:filterText
        bind:value={selectedTags}
        on:change={onTagChange}
        on:blur={onTagChange}
        on:clear={onTagClear}
        placeholder={$format('label.PLACEHOLDER.SELECT_OR_CREATE_NEW_TAG')}
        clearable={false}
        multiple
        showChevron
        searchable
      >
        <slot name="empty" slot="empty">
          <div class="empty">
            {#if !filterText.length}
              {$format('label.START_TYPING_TO_FIND_TAGS')}
            {:else if selectedTags?.map(({ value }) => value).includes(filterText)}
              {$format('label.TAG_ALREADY_EXISTS')}
            {:else}
              {@html $format('label.PRESS_ENTER_TO_CREATE_TAG', { values: { item: filterText } })}
            {/if}
          </div>
        </slot>
      </Select>
    </div>
  </div>

  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.CATEGORIES')}</b>
      <p>{$format('label.DESCRIPTION.CATEGORIES_CAMPAIGN')}</p>
    </div>
    <div class="section-controls">
      <Select
        bind:items={categories}
        bind:value={selectedCategories}
        on:change={onCategoryChange}
        on:clear={onCategoryChange}
        placeholder={$format('label.PLACEHOLDER.SELECT_CATEGORY')}
        clearable={false}
        searchable
        multiple
        showChevron
      />
    </div>
  </div>

  {#if folders.length}
    <div class="section-item">
      <div class="section-info">
        <b>{$format('label.ASSET_FOLDER')}</b>
        <p>{$format('label.DESCRIPTION.ASSET_FOLDER')}</p>
      </div>
      <div class="section-controls">
        <FolderTreeDropdown
          bind:folders
          selectable
          selected={selectedFolders}
          bind:selectedLabels
          {onSelect}
          expanded={false}
        >
          {@html selectedFolders.length
            ? `<i class="os-icon-folder"></i> ${selectedLabels[0]}`
            : $format('label.CHOOSE_FOLDER')}
        </FolderTreeDropdown>
      </div>
    </div>
  {/if}
</div>

<h2>{$format('label.AD_SETUP')}</h2>
<div class="section">
  <div class="tour-ad-setup"></div>
  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.CLICK_ACTION')}</b>
      <p>{$format('label.DESCRIPTION.CLICK_ACTION')}</p>
    </div>
    <div class="section-controls">
      <div class="action-controls click-action">
        <ButtonGroup>
          <Button active={campaign.linktype === LINK_TYPES.url.id} on:click={() => onClickAction(LINK_TYPES.url.id)}
            >{$format(LINK_TYPES.url.label)}</Button
          >
          <Button active={campaign.linktype === LINK_TYPES.doc.id} on:click={() => onClickAction(LINK_TYPES.doc.id)}
            >{$format(LINK_TYPES.doc.label)}</Button
          >
          <Button active={campaign.linktype === LINK_TYPES.video.id} on:click={() => onClickAction(LINK_TYPES.video.id)}
            >{$format(LINK_TYPES.video.label)}</Button
          >
        </ButtonGroup>

        {#if campaign.linktype === LINK_TYPES.url.id}
          <Input
            type="text"
            bind:value={campaign.url}
            invalid={validationError && campaign.url.length}
            feedback={errorMessage}
            placeholder={$format('label.ENTER_URL')}
            on:input={onValidation}
            on:blur={onUrlChange}
          />
        {:else if campaign.linktype === LINK_TYPES.doc.id}
          <Select
            bind:items={documentItems}
            bind:value={selectedDocument}
            on:change={onDocumentChange}
            placeholder={$format('label.PLACEHOLDER.SELECT_DOCUMENT')}
            class="select-element"
            clearable={false}
            showChevron
          >
            <svelte:fragment slot="item" let:item>
              <div class="item">
                <div class="item-detail">
                  <div class="image">
                    <img
                      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                      style="background: url(https://www.sendergen.com/v2/view/{item.hash}/thumbnail.gif) no-repeat center center;"
                      alt={item.filename}
                    />
                  </div>
                  <div class="info">
                    <b>{item.rulename}</b>
                    <small>{$format('label.FILE_SIZE')} {formatBytes(item.filesize)}</small>
                  </div>
                </div>
              </div>
            </svelte:fragment>
          </Select>
        {:else if campaign.linktype === LINK_TYPES.video.id}
          <Select
            bind:items={videoItems}
            bind:value={selectedVideo}
            on:change={onVideoChange}
            placeholder={$format('label.PLACEHOLDER.SELECT_VIDEO')}
            class="select-element"
            clearable={false}
            showChevron
          >
            <svelte:fragment slot="item" let:item>
              <div class="item">
                <div class="item-detail">
                  <div class="image">
                    <img
                      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                      style="background: url({item.thumbnail}) no-repeat center center;"
                      alt={item.title}
                    />
                  </div>
                  <div class="info">
                    <b>{item.rulename}</b>
                    <small>{$format('label.LENGTH')} {secondsToHms(item.duration)}</small>
                  </div>
                </div>
              </div>
            </svelte:fragment>
          </Select>
        {/if}
      </div>
    </div>
  </div>

  {#if campaign.linktype === LINK_TYPES.url.id && campaign?.url?.length && !validationError}
    <div class="section-item">
      <div class="section-info">
        <b>{$format('label.UTM_PARAMETERS')}</b>
        <p>{$format('label.DESCRIPTION.UTM_PARAMETERS')}</p>
      </div>
      <div class="section-controls">
        <div class="action-controls">
          <QueryParams params={campaign.params} onChange={onParamsChange} />
        </div>
      </div>
    </div>
  {/if}

  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.AD_TYPE')}</b>
      <p>
        {@html $format('label.DESCRIPTION.AD_TYPE_CHOICE')}
      </p>

      {#if campaign.adtype === AD_TYPES.image.id}
        <br />
        <p>
          {@html $format('label.DESCRIPTION.AD_TYPE_IMAGE')}
        </p>
      {/if}
    </div>
    <div class="section-controls">
      <div class="action-controls ad">
        <ButtonGroup>
          <Button active={campaign.adtype === AD_TYPES.text.id} on:click={(e) => onAdType(e)}
            >{$format(AD_TYPES.text.label)}</Button
          >
          <Button active={campaign.adtype === AD_TYPES.image.id} on:click={(e) => onAdType(e)}
            >{$format(AD_TYPES.image.label)}</Button
          >
        </ButtonGroup>

        {#if campaign.adtype === AD_TYPES.text.id}
          <div class="text-ad">
            <Input type="text" bind:value={campaign.textstring} placeholder={$format('label.PLACEHOLDER.AD_TEXT')} />

            <Input
              class="css-input"
              type="text"
              bind:value={campaign.textcss}
              placeholder={$format('label.PLACEHOLDER.AD_CSS')}
            />
          </div>
        {:else if campaign.adtype === AD_TYPES.image.id}
          <div class="image-ad">
            <label class="btn btn-secondary btn-sm" for="image-single">{$format('label.CHOOSE_FILE')}</label>
            <input
              id="image-single"
              name="image-single"
              hidden
              type="file"
              bind:files
              on:change={onFileUpload}
              accept=".png, .jpg, .jpeg, .gif"
            />

            {#if files.length}
              <div class="single-image">
                {#each Array.from(files) as file, index}
                  <div class="file-item">
                    <div class="image-preview">
                      <img src={URL.createObjectURL(file)} alt={file.name} />
                      <div class="details">
                        <small>{file.name}</small>
                        <small>{file.type} - ({formatBytes(file.size)})</small>
                      </div>
                    </div>
                    <div class="icon-actions">
                      <Icon name="trash-2" class="pointer" role="button" tabindex="0" on:click={onRemoveFile} />
                    </div>
                  </div>
                {/each}
              </div>
            {:else if campaign.metadata}
              <div class="campaign-image">
                <img src={campaign.previewURL} alt={campaign.rulename} />
                <div class="details">
                  <small>{campaign?.metadata.origfile}</small>
                  <small>
                    {campaign?.metadata?.mimetype} -

                    {#if campaign?.metadata?.imageinfo?.width}
                      {campaign?.metadata?.imageinfo?.width}x{campaign?.metadata.imageinfo.height}
                    {/if}

                    ({formatBytes(campaign?.metadata.filesize)})
                  </small>
                </div>
              </div>
            {/if}
          </div>
          <Input
            class="alt-text-input"
            type="text"
            bind:value={campaign.alttext}
            placeholder={$format('label.PLACEHOLDER.AD_IMAGE_ALT_TEXT')}
          />
        {/if}
      </div>
    </div>
  </div>

  {#if campaign.adtype === AD_TYPES.image.id}
    <div class="section-item">
      <div class="section-info">
        <b>{$format('label.INLINE_IMAGE')}</b>
        <p>{$format('label.DESCRIPTION.INLINE_IMAGE')}</p>
      </div>
      <div class="section-controls">
        <Switch bind:checked={campaign.inline} />
      </div>
    </div>
    <div class="section-item">
      <div class="section-info">
        <b>{$format('label.HIGH_RESOLUTION_IMAGE')}</b>
        <p>{$format('label.DESCRIPTION.HIGH_RESOLUTION_IMAGE')}</p>
      </div>
      <div class="section-controls">
        <Switch bind:checked={campaign.is2x} />
      </div>
    </div>
    <div class="section-item">
      <div class="section-info">
        <b>{$format('label.PLACEHOLDER_IMAGE')}</b>
        <p>{$format('label.DESCRIPTION.PLACEHOLDER_IMAGE')}</p>
      </div>
      <div class="section-controls">
        <Switch bind:checked={campaign.isplaceholder} />
      </div>
    </div>
  {/if}

  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.ALLOW_INTERNAL')}</b>
      <p>{$format('label.DESCRIPTION.ALLOW_INTERNAL')}</p>
    </div>
    <div class="section-controls">
      <Switch bind:checked={campaign.countinternal} />
    </div>
  </div>
  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.DISABLE_VIEW_TRACKING')}</b>
      <p>{$format('label.DESCRIPTION.DISABLE_VIEW_TRACKING')}</p>
    </div>
    <div class="section-controls">
      <Switch bind:checked={campaign.disableviews} />
    </div>
  </div>
</div>

<h2>{$format('label.TARGETING')}</h2>
<div class="section">
  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.RECIPIENT_BASED')}</b>
      <p>{$format('label.DESCRIPTION.RECIPIENT_BASED')}</p>
    </div>
    <div class="section-controls list-field">
      <ListField
        bind:items={recipients}
        validation={isValidEmail}
        validationError={$format('label.VALIDATION_ERROR_EMAIL_ADDRESS')}
        action={$format('label.ADD_EMAIL')}
      />
    </div>
  </div>
  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.DOMAIN_BASED')}</b>
      <p>{$format('label.DESCRIPTION.DOMAIN_BASED')}</p>
    </div>
    <div class="section-controls list-field">
      <ListField
        bind:items={domains}
        validation={isValidDomain}
        validationError={$format('label.VALIDATION_ERROR_NO_VALID_DOMAIN_CAMPAIGN')}
        action={$format('label.ADD_DOMAIN')}
      />
    </div>
  </div>
  <div class="section-item">
    <div class="section-info">
      <b>{$format('label.GROUP_BASED')}</b>
      <p>{$format('label.DESCRIPTION.GROUP_TARGETING')}</p>
    </div>
    <div class="section-controls list-field">
      <a href="{basepath}/marketing/targeting/groups" class="btn btn-secondary btn-sm" use:link
        >{$format('label.CONFIGURE')}</a
      >
    </div>
  </div>
</div>

<style lang="scss" src="./edit-form.scss"></style>
